/**
 * useScreenId — how a session screen says which screen it is.
 *
 * Each session component calls `useScreenId('see.pattern')` with the screen it
 * is showing right now. The session page reads the current value with
 * `useCurrentScreenId()` and draws it in ONE place. That split is the point:
 * a screen cannot forget to show its number, because screens never draw it.
 *
 * The value lives in a tiny module-level store rather than React context so a
 * component deep in the tree (the insight card inside Shift, the grounding
 * exercise inside Close) can report without every parent passing a prop down.
 *
 * A component that is only hosting another one passes `null` for that state
 * (Shift while the insight card is up) and the hosted component reports.
 *
 * A component that is mounted but not on screen reports nothing. The session
 * keeps every stage it has visited alive so Back can return to it as it was
 * (lib/sessionBack.ts), and a stamp that named a screen the person cannot see
 * would point a bug report at the wrong place.
 */
import { useContext, useEffect, useSyncExternalStore } from 'react'
import { SCREENS } from './screenIds'
import { ActiveContext } from './sessionBack'
import type { ScreenId, ScreenKey } from './screenIds'

let current: ScreenId | null = null
const listeners = new Set<() => void>()

function set(next: ScreenId | null) {
  if (current === next) return
  current = next
  listeners.forEach(notify => notify())
}

function subscribe(notify: () => void) {
  listeners.add(notify)
  return () => { listeners.delete(notify) }
}

/**
 * Report the screen this component is showing. Pass `null` when another
 * component is on screen in this one's place.
 */
export function useScreenId(key: ScreenKey | null): void {
  const active = useContext(ActiveContext)
  useEffect(() => {
    if (key === null || !active) return
    const id = SCREENS[key]
    set(id)
    // Only clear what this component set. When one screen hands over to the
    // next, the newcomer may already have reported by the time this runs.
    return () => { if (current === id) set(null) }
  }, [key, active])
}

/** The ID of the screen showing now, or null before any screen has reported. */
export function useCurrentScreenId(): ScreenId | null {
  return useSyncExternalStore(subscribe, () => current, () => null)
}
